import { Alert, AlertTitle, Box, Button } from "@mui/material";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import HttpService from "../../../hooks/Https-services";
import PageRoutes, { APIRoutes } from "../../../utils/constants";
import { getCurrentAuth } from "../../../hooks/useAxiosPrivate";
import useConfig from "../../../hooks/useConfig";

type InboxGift = {
  token: string;
  buyerName?: string;
  planTier?: string;
  billingModel?: string;
  termCount?: number;
  personalMessage?: string;
};

const termLabel = (billing?: string, term?: number) => {
  const n = Number(term) || 1;
  const unit = billing === "annual" ? "year" : "month";
  return `${n} ${unit}${n > 1 ? "s" : ""}`;
};

// HTPTV-636: on-site recovery for a recipient who missed or lost the gift email.
// Lists delivered, unredeemed gifts addressed to the logged-in user's email and
// redeems in one click (login already proves the email matches, and the backend
// re-enforces the email restriction on redeem). Renders nothing when there is
// no waiting gift, so it is safe to drop on any authenticated page.
const GiftInboxBanner = ({ onRedeemed }: { onRedeemed?: () => void }) => {
  const configData = useConfig();
  const navigate = useNavigate();
  const isLoggedIn = Boolean(getCurrentAuth()?.AccessToken);

  const [gifts, setGifts] = useState<InboxGift[]>([]);
  const [redeemingToken, setRedeemingToken] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoggedIn) return;
    (async () => {
      try {
        const res = await HttpService.get(APIRoutes.GiftSubscriptionInbox);
        setGifts(res?.data?.data?.gifts || []);
      } catch {
        // The banner is an enhancement; never let it block the page.
      }
    })();
  }, [isLoggedIn]);

  const redeem = async (g: InboxGift) => {
    setRedeemingToken(g.token);
    try {
      // Option B consent guard: if the recipient already has an active paid
      // plan, activating this gift stops that plan from auto-renewing. That
      // needs explicit consent, which lives on the redeem page, so route there
      // instead of redeeming in one click. Queuing behind another gift does
      // not stop any auto-renew, so that case keeps the 1-click flow. (HTPTV-636)
      const info = await HttpService.get(
        APIRoutes.GetGiftRedeemInfo.replace(":token", g.token)
      );
      if (info?.data?.data?.viewer?.hasActivePaidPlan) {
        navigate(PageRoutes.RedeemGiftPage.replace(":token", g.token));
        return;
      }
      const res = await HttpService.post(
        APIRoutes.RedeemGiftSubscription.replace(":token", g.token),
        {}
      );
      const d = res?.data?.data;
      setGifts((prev) => prev.filter((x) => x.token !== g.token));
      toast.success(
        d?.queued
          ? "Gift saved. It starts when your current access ends."
          : "Gift redeemed. Enjoy!"
      );
      onRedeemed?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Unable to redeem this gift.");
    } finally {
      setRedeemingToken(null);
    }
  };

  if (!isLoggedIn || !gifts.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {gifts.map((g) => {
        const tier = g.planTier
          ? g.planTier.charAt(0).toUpperCase() + g.planTier.slice(1)
          : configData?.personalChannel;
        return (
          <Alert
            key={g.token}
            icon={<CardGiftcardIcon fontSize="inherit" />}
            severity="info"
            sx={{ mb: 1, alignItems: "center" }}
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                disabled={redeemingToken === g.token}
                onClick={() => redeem(g)}
              >
                {redeemingToken === g.token ? "Redeeming…" : "Redeem now"}
              </Button>
            }
          >
            <AlertTitle sx={{ mb: 0.25 }}>You have a gift waiting</AlertTitle>
            {g.buyerName ? `${g.buyerName} sent you ` : "You've been gifted "}
            {tier} · {termLabel(g.billingModel, g.termCount)}
          </Alert>
        );
      })}
    </Box>
  );
};

export default GiftInboxBanner;
